import classnames from 'classnames'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { Popover } from 'reactstrap'
import { isOnlineOnly } from '../../../../../utility/programHelper'
import WaitlistActionModal from './modals/WaitlistActionModal'
import AddStudentForm from './forms/AddStudentForm'
import InviteToRegisterForm from './forms/InviteToRegisterForm'
import MoveToAnotherProgramForm from './forms/MoveToAnotherProgramForm'
import RemoveStudentForm from './forms/RemoveStudentForm'
import ProfileOverview from './ProfileOverview'
import WaitlistProgramSelectorModal from './modals/WaitlistProgramSelectorModal'

import { checkLicensePermission } from '../../../../../utility/communityLicense'
import * as Constants from '../../../../../misc/Constants'
import { ReactComponent as EllipsisIcon } from '../../../../../assets/images/common/icons/ellipsis-icon.svg'
import { ReactComponent as WarningIcon } from '../../../../../assets/images/common/icons/warning-icon.svg'

import classes from './WaitlistActions.module.scss'

const ACTIONS = {
  ADD_STUDENT: 'ADD_STUDENT',
  INVITE_TO_REGISTER: 'INVITE_TO_REGISTER',
  REMOVE_STUDENT: 'REMOVE_STUDENT',
}

const WaitlistActions = ({ waitlistEntry }) => {
  const { t } = useTranslation()
  const course = useSelector(state => state.programDashboard.program)
  const licenseType = useSelector(state => state.user.current_user.communities[0].license_type)

  const [action, setAction] = useState(null)
  const [showPopover, setShowPopover] = useState(false)

  // 'Move To Another Program' states
  const [isProgramSelectorOpen, setIsProgramSelectorOpen] = useState(false)
  const [selectedCourse, setSelectedCourse] = useState(null)

  const clearMoveStates = () => {
    setIsProgramSelectorOpen(false)
    setSelectedCourse(null)
  }

  const canAddToProgram = !checkLicensePermission(licenseType, [Constants.LICENSES.REVENUE_SHARE])
    && !isOnlineOnly(course.paymentMethod)

  return (
    <div id='WaitlistActions' className={classes.wrapper}>
      <button
        id={`ellipsis-button-${waitlistEntry.id}`}
        className={classnames('w-100 d-flex justify-content-center align-items-center', classes.ellipsisButton)}
      >
        <EllipsisIcon className={classes.icon} />
      </button>
      <Popover
        placement='bottom'
        target={`ellipsis-button-${waitlistEntry.id}`}
        isOpen={showPopover}
        toggle={() => setShowPopover(prev => !prev)}
        popperClassName='popover-style'
        trigger='legacy'
      >
        <div className={classnames('d-flex flex-column align-items-start', classes.actions)}>
          {canAddToProgram && (
            <button
              onClick={() => { setAction(ACTIONS.ADD_STUDENT); setShowPopover(false) }}
            >
              {t('programs.manage.waitlistActions.addToProgram')}
            </button>
          )}
          <button onClick={() => { setAction(ACTIONS.INVITE_TO_REGISTER); setShowPopover(false) }}>
            {waitlistEntry.courseInvitationStatus !== 'not_found'
              ? t('programs.manage.waitlistActions.resendRegistrationLink')
              : t('programs.manage.waitlistActions.sendRegistrationLink')}
          </button>
          <button onClick={() => { setIsProgramSelectorOpen(true); setShowPopover(false) }}>
            {t('programs.manage.waitlistActions.moveToAnotherProgram')}
          </button>
          <Link
            to='/announcements/new-campaign'
            state={{ smartToggle: 'email', students: [waitlistEntry.user] }}
          >
            {t('programs.manage.waitlistActions.sendAnEmail')}
          </Link>
          <button
            className={classes.danger}
            onClick={() => { setAction(ACTIONS.REMOVE_STUDENT); setShowPopover(false) }}
          >
            {t('programs.manage.waitlistActions.removeStudent')}
          </button>
        </div>
      </Popover>

      {action === ACTIONS.ADD_STUDENT && (
        <WaitlistActionModal
          isOpen={action === ACTIONS.ADD_STUDENT}
          onClose={() => setAction(null)}
          student={waitlistEntry.user}
          title={t('programs.manage.waitlistActions.addToProgram')}
          subtitle={t('programs.manage.waitlistActions.addToProgramSubtitle')}
          form={
            <AddStudentForm
              course={course}
              waitlistEntry={waitlistEntry}
              closeModal={() => setAction(null)}
            />
          }
        />
      )}

      {action === ACTIONS.INVITE_TO_REGISTER && (
        <WaitlistActionModal
          isOpen={action === ACTIONS.INVITE_TO_REGISTER}
          onClose={() => setAction(null)}
          student={waitlistEntry.user}
          title={t('programs.manage.waitlistActions.sendRegistrationLink')}
          subtitle={t('programs.manage.waitlistActions.sendRegistrationLinkSubtitle')}
          subHeaderSection={
            <div className='d-flex align-items-center mb-4'>
              <WarningIcon className={classes.expiryWarningIcon} />
              <p className={classes.expiryWarningText}>
                {t('programs.manage.waitlistActions.registrationLinkExpiry')}
              </p>
            </div>
          }
          form={
            <InviteToRegisterForm
              course={course}
              waitlistEntry={waitlistEntry}
              closeModal={() => setAction(null)}
            />
          }
        />
      )}

      {isProgramSelectorOpen && (
        <WaitlistProgramSelectorModal
          title={t('programs.manage.waitlistActions.moveStudentFromWaitlist')}
          isOpen={isProgramSelectorOpen}
          currentProgram={course}
          selectedProgram={selectedCourse}
          onSelectProgram={setSelectedCourse}
          selectedStudent={waitlistEntry.user}
          onClose={() => { setIsProgramSelectorOpen(false); clearMoveStates() }}
          onProceed={() => { setAction(ACTIONS.MOVE_TO_ANOTHER_PROGRAM); setIsProgramSelectorOpen(false) }}
        />
      )}

      {action === ACTIONS.MOVE_TO_ANOTHER_PROGRAM && (
        <WaitlistActionModal
          isOpen={action === ACTIONS.MOVE_TO_ANOTHER_PROGRAM}
          onClose={() => { setAction(null); clearMoveStates() }}
          student={waitlistEntry.user}
          title={t('programs.manage.waitlistActions.confirmMoveFromWaitlist')}
          subtitle={t('programs.manage.waitlistActions.confirmMoveSubtitle')}
          customInfoPanel={
            <>
              <ProfileOverview student={waitlistEntry.user} />
              <div className={classes.divider} />
              <div className='my-2'>
                <p className='font-weight-bolder mb-0'>
                  {t('programs.manage.waitlistActions.fromWaitlist')}
                </p>
                <span>{course.name}</span>
              </div>
              <div className='mb-2'>
                <p className='font-weight-bolder mb-0'>
                  {t('programs.manage.waitlistActions.toNewProgram')}
                </p>
                <span>{selectedCourse?.name || ''}</span>
              </div>
              {selectedCourse && isOnlineOnly(selectedCourse.paymentMethod) && (
                <div className='d-flex align-items-center mb-2'>
                  <WarningIcon className={classes.onlinePaymentWarningIcon} />
                  <p className={classes.onlinePaymentWarningText}>
                    {t('programs.manage.waitlistActions.onlinePaymentOnly')}
                  </p>
                </div>
              )}
            </>
          }
          form={
            <MoveToAnotherProgramForm
              course={course}
              selectedCourse={selectedCourse}
              waitlistEntry={waitlistEntry}
              closeModal={() => { setAction(null); clearMoveStates() }}
            />
          }
        />
      )}

      {action === ACTIONS.REMOVE_STUDENT && (
        <WaitlistActionModal
          isOpen={action === ACTIONS.REMOVE_STUDENT}
          onClose={() => setAction(null)}
          student={waitlistEntry.user}
          title={t('programs.manage.waitlistActions.removeFromWaitlist')}
          subtitle={t('programs.manage.waitlistActions.removeFromWaitlistSubtitle')}
          form={
            <RemoveStudentForm
              course={course}
              waitlistEntry={waitlistEntry}
              closeModal={() => setAction(null)}
            />
          }
        />
      )}
    </div>
  )
}

WaitlistActions.propTypes = {
  waitlistEntry: PropTypes.shape({
    id: PropTypes.number,
    courseInvitationStatus: PropTypes.string,
    user: PropTypes.shape({
      id: PropTypes.number,
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      invited: PropTypes.bool,
    }),
  }),
}

export default WaitlistActions
